import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import {
  WellInfoSidebar as WellInfoSidebarTypes,
  WellInfoSidebarStore,
} from 'src/features/well-info-sidebar/well-info-sidebar.store';
import EditIcon from 'src/shared/assets/icons/pencil.svg';
import { Button } from 'src/shared/components/button';
import { Loader } from 'src/shared/components/loader';
import { Sidebar } from 'src/shared/components/sidebar/sidebar';
import { SwitchButtonGroup } from 'src/shared/components/switch-button-group';
import { useStore } from 'src/store';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  FULL_ACCESS_ROLE,
  GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
} from 'src/store/roles-store/roles';

import { InfoSidebarTabContent } from '../graph-info-sidebar/info-sidebar-tab-component/info-sidebar-tab-component';

import styles from './well-info-sidebar.module.scss';

export const DEFAULT_WELL_SIDEBAR_TAB = WellInfoSidebarTypes.Tabs.mainInfo;

interface Props {
  isOpen: boolean;
  geoTaskId: number | string;
  onClose: VoidFunction;
  isEditButtonDisabled?: boolean;
  disableEditing?: boolean;
  onEdit?(wellId: number | string): void;
  initialActiveTab?: WellInfoSidebarTypes.Tabs;
}

export const WellInfoSidebar = observer(function WellInfoSidebar({
  isOpen,
  geoTaskId,
  initialActiveTab = DEFAULT_WELL_SIDEBAR_TAB,
  isEditButtonDisabled,
  onEdit,
  onClose,
  disableEditing,
}: Props) {
  const rootStore = useStore();
  const [store] = useState(() => new WellInfoSidebarStore(rootStore));
  const [activeTabName, setActiveTabName] = useState<string>(initialActiveTab);

  const isWellEditingAvailable = useMemo(() => {
    if (rootStore.planVersionsService.currentVersion?.data.isArchive) {
      return rootStore.rolesStore.hasAccess([
        FULL_ACCESS_ROLE,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
      ]);
    }

    if (rootStore.planVersionsService.currentVersion?.data.versionType === PlanVersionType.current) {
      return false;
    }

    return rootStore.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_EDIT]);
  }, [
    rootStore.planVersionsService.currentVersion?.data.isArchive,
    rootStore.planVersionsService.currentVersion?.data.versionType,
    rootStore.rolesStore,
  ]);

  useEffect(() => {
    if (!store.viewProvider) {
      store.init();
    }
  }, [store, store.init]);

  useEffect(() => {
    if (store.viewProvider) {
      store.loadWell(geoTaskId);
    }
  }, [geoTaskId, store.viewProvider, store]);

  useEffect(() => {
    setActiveTabName(initialActiveTab);
  }, [initialActiveTab]);

  const handleEdit = () => {
    onClose();
    onEdit?.(geoTaskId);
  };

  const getTitle = (): string | undefined => {
    if (store.wellStore.isLoading) {
      return;
    }

    if (store.wellStore.selectedWell && store.viewProvider) {
      return store.getSidebarTitle(store.wellStore.selectedWell) || '';
    }
  };

  const tabs = store.viewProvider?.tabsSwitchButtonsGroup || [];
  const activeTab = store.viewProvider?.tabs.find((tab) => tab.fieldId === activeTabName);

  return (
    <Sidebar isOpened={isOpen} onClose={onClose} title={getTitle()} size="xl" key={geoTaskId}>
      <div className={styles.header}>
        <SwitchButtonGroup
          items={tabs}
          defaultOptionKey={activeTabName}
          className={styles.tabs}
          disabled={store.wellStore.isLoading}
          onChange={setActiveTabName}
        />

        {!disableEditing && isWellEditingAvailable && (
          <Button
            icon={<EditIcon className={styles.editIcon} />}
            className={styles.editButton}
            disabled={isEditButtonDisabled || !!rootStore.jobsStore.currentImportVersionJob}
            onClick={handleEdit}
          />
        )}
      </div>

      <div className={styles.tabContent}>
        {store.wellStore.isLoading || store.isLoading ? (
          <Loader className={styles.loader} />
        ) : (
          <div className={styles.tabContentInner}>
            <InfoSidebarTabContent activeTab={activeTab} store={store} />
          </div>
        )}
      </div>
    </Sidebar>
  );
});
